/**
 * useProducts Hook - Story 6.2: Create My Products Page
 * React Query hook for fetching and managing products with caching
 *
 * OPTIMIZED: Single API call - products endpoint returns all data
 * (quantities, inquiry counts) - no separate API calls needed
 */

import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';
import { fetchProducts } from '../services/productsService';

// Low stock threshold constant - products below this kg are auto-deactivated
// When available quantity < 20 kg: product becomes INACTIVE and disappears from list
// When available quantity >= 20 kg: product becomes ACTIVE and appears in list
const LOW_STOCK_THRESHOLD = 20;

import type {
  ProductFilters,
  ProductRow,
  UseProductsReturn,
  ApiErrorResponse,
} from '../types/products.types';

/**
 * Query key factory for products
 * Used for cache management and query invalidation
 */
export const productsKeys = {
  all: ['products'] as const,
  filters: (filters: ProductFilters) => [...productsKeys.all, 'filters', filters] as const,
  byPage: (page: number, pageSize: number) =>
    [...productsKeys.all, 'byPage', page, pageSize] as const,
  search: (search: string) => [...productsKeys.all, 'search', search] as const,
} as const;

/**
 * Convert Product data to ProductRow for table display
 * Uses backend-calculated quantities (totalQuantityKg, availableQuantityKg) directly
 */
function convertToProductRow(product: any): ProductRow {
  // Extract first image: handle both string[] and object[] formats
  let firstImage: string | undefined;
  if (product.images && product.images.length > 0) {
    const firstImageItem = product.images[0];
    // If it's an object with 'url' property, use that; otherwise treat as string URL
    let imageUrl = typeof firstImageItem === 'object' && firstImageItem?.url
      ? firstImageItem.url
      : typeof firstImageItem === 'string'
        ? firstImageItem
        : undefined;

    // Image URLs like /uploads/products/{id}/images/{filename} are served
    // via Vite proxy (/uploads → file-service) in dev, and via gateway in prod
    firstImage = imageUrl;
  }

  // Use backend-calculated quantities directly (from ProductResponse DTO)
  // totalQuantityKg = SUM of mfg_production_batch.output_quantity_kg
  // availableQuantityKg = dung_products.quantity_available
  const totalQty = product.totalQuantityKg ?? product.totalQuantity ?? 0;
  const availableQty = product.availableQuantityKg ?? product.availableQuantity ?? product.quantityAvailable ?? 0;

  console.log('[convertToProductRow] Product:', product.id, product.productName || product.name,
    '| Total:', totalQty, '| Available:', availableQty);

  return {
    id: product.id,
    image: firstImage,
    name: product.name || product.productName, // Backend sends 'productName', frontend expects 'name'
    category: product.category,
    status: product.status,
    inquiries: product.inquiryCount || product.totalInquiries || 0,
    createdDate: new Date(product.createdAt).toLocaleDateString('en-IN'),
    price: product.price,
    unit: 'kg', // Always force unit to be 'kg' for all products
    totalQuantity: totalQty,
    availableQuantity: availableQty,
  };
}

/**
 * useProducts Hook
 * Fetches seller's products with React Query caching
 *
 * OPTIMIZED: Single API call - products endpoint returns all data including:
 * - totalQuantityKg (from mfg_production_batch)
 * - availableQuantityKg (from dung_products.quantity_available)
 * - inquiryCount (from product_inquiries count)
 *
 * @param filters - Product filter options including pagination
 * @returns Product data, loading/error states, and refetch function
 */
export function useProducts(filters: ProductFilters): UseProductsReturn {
  // Single API call - products endpoint returns all data
  const {
    data,
    isLoading,
    isError,
    error: queryError,
    refetch,
  } = useQuery({
    queryKey: productsKeys.filters(filters),
    queryFn: () => fetchProducts(filters),
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes (formerly cacheTime)
    retry: 3,
    retryDelay: (attemptIndex: number) => Math.min(1000 * 2 ** attemptIndex, 30000),
    refetchOnWindowFocus: true,
    enabled: true,
  });

  // Process products from single API response
  const products = useMemo(() => {
    console.log('[useProducts] Processing products from single API call');

    return (data?.data || [])
      .filter((product: any) => product.status !== 'ARCHIVED') // Filter out soft-deleted products
      .map((product: any) => {
        // Convert to ProductRow - all data comes from products API
        const row = convertToProductRow(product);
        return row;
      })
      .filter((product: ProductRow) => {
        // Client-side filter based on BOTH status and quantity thresholds
        // qty < 20: auto-inactive (yellow/red) regardless of DB status
        // qty >= 20: respects DB status (user can toggle)
        const qty = product.availableQuantity ?? 0;

        if (filters.status === 'ALL') {
          return true;
        }

        if (filters.status === 'ACTIVE') {
          // Active = sufficient stock (>= 20 kg) AND DB status is ACTIVE
          return qty >= LOW_STOCK_THRESHOLD && product.status === 'ACTIVE';
        }

        // INACTIVE filter: low/zero stock OR manually deactivated
        // - qty < 20: always inactive (auto-deactivated due to low stock)
        // - qty >= 20 AND status INACTIVE: manually deactivated by user
        return qty < LOW_STOCK_THRESHOLD || product.status === 'INACTIVE';
      });
  }, [data?.data, filters.status]);

  const pagination = data?.pagination || {
    page: filters.page,
    pageSize: filters.pageSize,
    totalItems: 0,
    totalPages: 0,
    hasNext: false,
    hasPrev: false,
  };

  // Convert error to standard format
  const error: ApiErrorResponse | null = queryError
    ? {
        message: queryError instanceof Error ? queryError.message : 'Failed to fetch products',
        statusCode: 500,
        errorCode: 'FETCH_ERROR',
      }
    : null;

  const refetchProducts = useCallback(async () => {
    await refetch();
  }, [refetch]);

  return {
    products,
    isLoading,
    isError,
    error,
    totalItems: pagination.totalItems,
    totalPages: pagination.totalPages,
    currentPage: pagination.page,
    refetch: refetchProducts,
    hasNextPage: pagination.hasNext,
    hasPreviousPage: pagination.hasPrev,
  };
}

/**
 * Prefetch products for pagination
 * Useful for preloading next/previous pages
 *
 * @param filters - Filter options
 */
export async function prefetchProducts(filters: ProductFilters) {
  const queryClient = useQueryClient();
  await queryClient.prefetchQuery({
    queryKey: productsKeys.filters(filters),
    queryFn: () => fetchProducts(filters),
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Invalidate products cache
 * Triggers refetch of all product queries
 */
export function invalidateProducts() {
  const queryClient = useQueryClient();
  queryClient.invalidateQueries({
    queryKey: productsKeys.all,
  });
}

/**
 * Invalidate specific page cache
 *
 * @param page - Page number to invalidate
 * @param pageSize - Page size
 */
export function invalidateProductsPage(page: number, pageSize: number) {
  const queryClient = useQueryClient();
  queryClient.invalidateQueries({
    queryKey: productsKeys.byPage(page, pageSize),
  });
}

/**
 * Refetch products
 * Manually trigger refetch of current page
 */
export async function refetchProducts(filters: ProductFilters) {
  const queryClient = useQueryClient();
  await queryClient.refetchQueries({
    queryKey: productsKeys.filters(filters),
  });
}

/**
 * Export API error class for use in components
 */
export { ProductsApiError } from '../services/productsService';

/**
 * Export types for use in components
 */
export type { UseProductsReturn, ProductFilters, ProductRow, ApiErrorResponse };
